import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  EMPTY_TASK_FILTERS,
  LAYOUT,
  clamp,
  layoutZone,
  matchesTaskFilters,
  snapToGrid,
  type Client,
  type Stage,
  type Task,
  type TaskWorklist,
  type TaskFilters,
  type User,
  type Worklist,
} from "@board/shared";
import { api } from "./api";
import { shrinkImage } from "./shrinkImage";
import { MARKER_COLORS, colorOf, useBoard, type MarkerColor, type Tool } from "./store";
import { useCanvasTransform, type Transform } from "./useCanvasTransform";
import { arrowPath } from "./handDrawn";
import { Zone } from "./Zone";
import { ClientEditor } from "./ClientEditor";
import { TaskSheetHost } from "./TaskSheet";
import { TaskFilterBar } from "./TaskFilterBar";
import { RecycleBin } from "./RecycleBin";
import { TeamManager } from "./TeamManager";
import { InkLayer, type InkRegion } from "./InkLayer";
import { TextLayer } from "./TextLayer";
import { AttachmentLayer } from "./AttachmentLayer";
import { WorklistZone } from "./WorklistZone";

function clientSlugFromPath(): string | null {
  const encoded = window.location.pathname.match(/^\/c\/([^/]+)\/?$/)?.[1];
  if (!encoded) return null;
  try {
    return decodeURIComponent(encoded);
  } catch {
    return null;
  }
}

const completedKey = (kind: "client" | "worklist", id: string) => `${kind}:${id}`;
const NO_EXPANDED_COMPLETED: ReadonlySet<string> = new Set();

function boundsOf(
  clients: Client[],
  tasks: Task[],
  worklists: Worklist[] = [],
  links: TaskWorklist[] = [],
  expandedCompleted: ReadonlySet<string> = NO_EXPANDED_COMPLETED,
) {
  const zones = [
    ...clients.map((client) => ({ ...client, kind: "client" as const,
      tasks: tasks.filter((task) => task.client_id === client.id) })),
    ...worklists.map((worklist) => ({
      ...worklist,
      kind: "worklist" as const,
      tasks: tasks.filter((task) => task.worklist_id === worklist.id ||
        links.some((link) => link.worklist_id === worklist.id && link.task_id === task.id)),
    })),
  ];
  if (zones.length === 0) return null;
  const bottoms = zones.map((client) => {
    const required = layoutZone(client.width, client.tasks, undefined, {
      collapseCompleted: !expandedCompleted.has(completedKey(client.kind, client.id)),
    }).requiredHeight;
    return client.canvas_y + Math.max(client.height, required);
  });
  const left = Math.min(...zones.map((client) => client.canvas_x));
  const top = Math.min(...zones.map((client) => client.canvas_y));
  const right = Math.max(...zones.map((client) => client.canvas_x + client.width));
  return { x: left, y: top, width: right - left, height: Math.max(...bottoms) - top };
}

/**
 * Whether the space bar is being held.
 *
 * Space-drag is the one gesture that outranks whatever is in your hand: with a
 * marker, an eraser or a sticky pad selected there is otherwise no way to move
 * the board without putting the tool down first, which is exactly when you least
 * want to.
 *
 * Two things it must not do. It must not fire while someone is typing — a space
 * in a task title is a space, not a pan — so an editable target is ignored, and
 * a note being written keeps its spaces. And it must not stay stuck on: holding
 * space while switching windows means the keyup lands somewhere else, so a lost
 * window drops it.
 */
function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return Boolean(el?.closest?.("input, textarea, select, [contenteditable='true']"));
}

function useSpaceHeld(): boolean {
  const [held, setHeld] = useState(false);

  useEffect(() => {
    const down = (event: KeyboardEvent) => {
      if (event.code !== "Space" || event.repeat || isTyping(event.target)) return;
      // Otherwise space scrolls the page, or presses whichever button in the
      // frame happens to have focus.
      event.preventDefault();
      setHeld(true);
    };
    const up = (event: KeyboardEvent) => {
      if (event.code === "Space") setHeld(false);
    };
    const drop = () => setHeld(false);

    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", drop);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", drop);
    };
  }, []);

  return held;
}

/**
 * The board as an object: an aluminium-framed whiteboard filling the window,
 * with a slim pen rail below it holding the markers, eraser, sticky pad and
 * label tile. The rail is the tool selector — you take a pen off it rather
 * than pressing a button that looks like one — and everything on it is also on
 * the right-click menu, for when the pointer is already where the work is.
 */
export function BoardApp({ user }: { user: User }) {
  const surface = useRef<HTMLDivElement>(null);
  const view = useCanvasTransform(surface);

  const status = useBoard((s) => s.status);
  const clients = useBoard((s) => s.clients);
  const worklists = useBoard((s) => s.worklists);
  const taskWorklists = useBoard((s) => s.taskWorklists);
  const tasks = useBoard((s) => s.tasks);
  const users = useBoard((s) => s.users);
  const load = useBoard((s) => s.load);
  const refresh = useBoard((s) => s.refresh);
  const select = useBoard((s) => s.select);
  const tool = useBoard((s) => s.tool);
  const setTool = useBoard((s) => s.setTool);
  const openTask = useBoard((s) => s.openTask);
  const isAdmin = useBoard((s) => s.isAdmin());
  const selectObject = useBoard((s) => s.selectObject);
  const undo = useBoard((s) => s.undo);
  const redo = useBoard((s) => s.redo);
  const canUndo = useBoard((s) => s.historyPast.length > 0);
  const canRedo = useBoard((s) => s.historyFuture.length > 0);
  const historyBusy = useBoard((s) => s.historyBusy);
  const historyProblem = useBoard((s) => s.historyProblem);
  const moveTaskStage = useBoard((s) => s.moveTaskStage);
  const linkTaskToWorklist = useBoard((s) => s.linkTaskToWorklist);

  const focusedClientId = useBoard((s) => s.focusedClientId);
  const focusedTaskId = useBoard((s) => s.focusedTaskId);
  const focusClient = useBoard((s) => s.focusClient);

  const spaceHeld = useSpaceHeld();
  const [adding, setAdding] = useState(false);
  const [addingWorklist, setAddingWorklist] = useState(false);
  const [editingWorklistId, setEditingWorklistId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<{ kind: "client" | "worklist"; id: string; stage: Stage } | null>(null);
  const [dragProblem, setDragProblem] = useState("");
  // A board point the right-click menu asked to attach a file at. Held here
  // rather than in the menu, because the menu closes the moment it is used.
  const [attachPoint, setAttachPoint] = useState<{ x: number; y: number } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [teamOpen, setTeamOpen] = useState(false);
  const [recycleOpen, setRecycleOpen] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [filters, setFilters] = useState<TaskFilters>(EMPTY_TASK_FILTERS);
  const [expandedCompleted, setExpandedCompleted] = useState<Set<string>>(() => new Set());
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const previousView = useRef<Transform | null>(null);
  const recycleCount = useBoard((s) => s.recycleCount);
  const routeReady = useRef(false);
  const longPress = useRef<{
    pointer: number;
    x: number;
    y: number;
    timer: number;
  } | null>(null);

  const clearLongPress = useCallback((pointer?: number) => {
    const active = longPress.current;
    if (!active || (pointer !== undefined && active.pointer !== pointer)) return;
    window.clearTimeout(active.timer);
    longPress.current = null;
  }, []);

  const toggleCompleted = useCallback((key: string) => {
    setExpandedCompleted((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  useEffect(() => () => clearLongPress(), [clearLongPress]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (status !== "ready") return;
    const events = new EventSource("/api/events");
    let refreshTimer = 0;
    let opened = false;
    const queueRefresh = () => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => void refresh(), 110);
    };
    events.addEventListener("board_changed", queueRefresh);
    events.addEventListener("open", () => {
      if (opened) queueRefresh();
      opened = true;
    });
    return () => {
      window.clearTimeout(refreshTimer);
      events.close();
    };
  }, [refresh, status]);

  // The focused client is rendered at a width taken from the viewport, so keep
  // that measurement current.
  useEffect(() => {
    const el = surface.current;
    if (!el) return;
    const measure = () => {
      const box = el.getBoundingClientRect();
      setViewport({ width: box.width, height: box.height });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const focused = clients.find((c) => c.id === focusedClientId) ?? null;
  // Read from the store rather than held in state, so an edit made here shows
  // in the open form rather than the form holding the row it opened with.
  const editingClient = clients.find((c) => c.id === editingId) ?? null;

  /**
   * In focus mode the client is rendered wide rather than zoomed. Zooming a
   * 600-wide column to fit a wide screen is limited by its height and leaves a
   * thin strip down the middle; widening it reflows the bands several notes
   * across and actually fills the screen.
   *
   * Width is a render-time argument to layoutZone and is never persisted, so
   * this changes nothing on the row — leaving focus restores the board exactly.
   */
  const boardClients = useMemo(
    () => filters.clientId === "all" ? clients : clients.filter((client) => client.id === filters.clientId),
    [clients, filters.clientId],
  );

  const effectiveFilters = useMemo(
    () => ({ ...filters, clientId: focused?.id ?? filters.clientId }),
    [filters, focused?.id],
  );
  const boardFilteredTasks = useMemo(
    () => tasks.filter((task) => matchesTaskFilters(task, filters, user.id)),
    [filters, tasks, user.id],
  );
  const filteredTasks = useMemo(
    () => tasks.filter((task) => matchesTaskFilters(task, effectiveFilters, user.id)),
    [effectiveFilters, tasks, user.id],
  );
  const visibleTaskIds = useMemo(() => new Set(filteredTasks.map((task) => task.id)), [filteredTasks]);
  const scopedTaskCount = useMemo(
    () => tasks.filter((task) => effectiveFilters.clientId === "all" || task.client_id === effectiveFilters.clientId).length,
    [effectiveFilters.clientId, tasks],
  );

  const shown = useMemo(() => {
    if (!focused) return boardClients;
    const width = clamp(
      snapToGrid(viewport.width > 0 ? viewport.width - 160 : LAYOUT.zone.defaultWidth),
      LAYOUT.zone.minWidth,
      LAYOUT.zone.maxWidth,
    );
    return [{ ...focused, canvas_x: 0, canvas_y: 0, width }];
  }, [focused, boardClients, viewport.width]);
  const shownWorklists = useMemo(() => focused ? [] : worklists, [focused, worklists]);
  const visibleTaskWorklists = useMemo(
    () => taskWorklists.filter((link) => visibleTaskIds.has(link.task_id)),
    [taskWorklists, visibleTaskIds],
  );

  /**
   * Bounding box of every column, for zoom-to-fit. The height has to be the one
   * a column actually renders at, not the one on its row — bands grow with
   * their notes, and the stored height is not updated until auto-tidy lands.
   */
  const boardContentBox = useMemo(
    () => boundsOf(boardClients, boardFilteredTasks, worklists, taskWorklists, expandedCompleted),
    [boardClients, boardFilteredTasks, expandedCompleted, taskWorklists, worklists],
  );
  const activeContentBox = useMemo(
    () => boundsOf(shown, filteredTasks, shownWorklists, visibleTaskWorklists, expandedCompleted),
    [expandedCompleted, filteredTasks, shown, shownWorklists, visibleTaskWorklists],
  );
  const inkRegions = useMemo<InkRegion[]>(() => shown.map((client) => {
    const rows = filteredTasks.filter((task) => task.client_id === client.id);
    return {
      clientId: client.id,
      x: client.canvas_x,
      y: client.canvas_y,
      width: client.width,
      height: Math.max(client.height, layoutZone(client.width, rows, undefined, {
        collapseCompleted: !expandedCompleted.has(completedKey("client", client.id)),
      }).requiredHeight),
    };
  }), [expandedCompleted, filteredTasks, shown]);

  /** Phones open a column at readable width and at its top, not as a thumbnail. */
  const fitClientOnCompactScreen = useCallback((client: Client): boolean => {
    if (viewport.width <= 0 || viewport.height <= 0) return false;
    const compact = viewport.width <= 820 || (viewport.width <= 1000 && viewport.height <= 500);
    if (!compact) return false;
    const gutter = 22;
    const scale = clamp((viewport.width - gutter * 2) / client.width, 0.1, 1.2);
    view.setView({
      scale,
      x: viewport.width / 2 - (client.canvas_x + client.width / 2) * scale,
      y: gutter - client.canvas_y * scale,
    });
    return true;
  }, [view.setView, viewport.height, viewport.width]);

  const enterClient = useCallback(
    (clientId: string, writeHistory = true, rememberView = true) => {
      const client = clients.find((candidate) => candidate.id === clientId);
      if (!client) return;
      if (rememberView && !focusedClientId) previousView.current = view.transform;
      focusClient(clientId);
      const path = `/c/${encodeURIComponent(client.slug)}`;
      if (writeHistory && window.location.pathname !== path) window.history.pushState({}, "", path);
    },
    [clients, focusClient, focusedClientId, view.transform],
  );

  /**
   * Double-clicking a note: focus its client, then open the note. In that
   * order, because focusing clears whatever task is open — a client's board
   * arriving with the workspace shut would lose the note that was asked for.
   */
  const expandTask = useCallback(
    (clientId: string, taskId: string) => {
      enterClient(clientId);
      openTask(taskId);
    },
    [enterClient, openTask],
  );

  const leaveClient = useCallback(
    (writeHistory = true) => {
      focusClient(null);
      if (writeHistory && window.location.pathname !== "/") window.history.pushState({}, "", "/");
      const saved = previousView.current;
      previousView.current = null;
      window.requestAnimationFrame(() => {
        if (saved) view.setView(saved);
        else view.fitTo(boardContentBox);
      });
    },
    [boardContentBox, focusClient, view.fitTo, view.setView],
  );

  useEffect(() => {
    if (status !== "ready" || routeReady.current) return;
    routeReady.current = true;
    const slug = clientSlugFromPath();
    const client = clients.find((candidate) => candidate.slug === slug);
    if (client) enterClient(client.id, false, false);
    else if (window.location.pathname !== "/") window.history.replaceState({}, "", "/");
  }, [clients, enterClient, status]);

  useEffect(() => {
    const onPopState = () => {
      const slug = clientSlugFromPath();
      const client = useBoard.getState().clients.find((candidate) => candidate.slug === slug);
      if (client) enterClient(client.id, false);
      else leaveClient(false);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [enterClient, leaveClient]);

  useEffect(() => {
    if (!focusedClientId) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || focusedTaskId) return;
      if ((event.target as HTMLElement)?.closest("input, textarea, .sheet")) return;
      leaveClient();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusedClientId, focusedTaskId, leaveClient]);

  useEffect(() => {
    if (adding || addingWorklist || editingClient || editingWorklistId || teamOpen || recycleOpen || focusedTaskId) return;
    const onToolKey = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if ((event.target as HTMLElement)?.closest("input, textarea, select, [contenteditable=true]")) return;
      const next: Record<string, Tool> = {
        v: "select", m: "pen", e: "eraser", s: "sticky", t: "text", a: "attach",
      };
      const selected = next[event.key.toLowerCase()];
      if (!selected) return;
      event.preventDefault();
      setMenu(null);
      setTool(selected);
    };
    window.addEventListener("keydown", onToolKey);
    return () => window.removeEventListener("keydown", onToolKey);
  }, [adding, addingWorklist, editingClient, editingWorklistId, focusedTaskId, recycleOpen, setTool, teamOpen]);

  useEffect(() => {
    if (adding || addingWorklist || editingClient || editingWorklistId || teamOpen || recycleOpen || focusedTaskId) return;
    const onHistoryKey = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
      if ((event.target as HTMLElement)?.closest("input, textarea, select, [contenteditable=true]")) return;
      const key = event.key.toLowerCase();
      const wantsUndo = key === "z" && !event.shiftKey;
      const wantsRedo = (key === "z" && event.shiftKey) || key === "y";
      if (!wantsUndo && !wantsRedo) return;
      event.preventDefault();
      if (wantsUndo) void undo();
      else void redo();
    };
    window.addEventListener("keydown", onHistoryKey);
    return () => window.removeEventListener("keydown", onHistoryKey);
  }, [adding, addingWorklist, editingClient, editingWorklistId, focusedTaskId, recycleOpen, redo, teamOpen, undo]);

  useEffect(() => {
    const client = shown[0];
    if (!focused || !client) return;
    if (fitClientOnCompactScreen(client)) return;
    const rows = filteredTasks.filter((task) => task.client_id === client.id);
    const box = { x: 0, y: 0, width: client.width, height: Math.max(client.height, layoutZone(client.width, rows, undefined, {
      collapseCompleted: !expandedCompleted.has(completedKey("client", client.id)),
    }).requiredHeight) };
    const frame = window.requestAnimationFrame(() => view.fitTo(box));
    return () => window.cancelAnimationFrame(frame);
  }, [expandedCompleted, filteredTasks, fitClientOnCompactScreen, focused, shown, view.fitTo]);

  const previousClientFilter = useRef(filters.clientId);
  useEffect(() => {
    if (previousClientFilter.current === filters.clientId) return;
    previousClientFilter.current = filters.clientId;
    if (focused || !boardContentBox) return;
    const first = boardClients[0];
    if (first && fitClientOnCompactScreen(first)) return;
    const frame = window.requestAnimationFrame(() =>
      view.fitTo(boardContentBox),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [boardClients, boardContentBox, filters.clientId, fitClientOnCompactScreen, focused, view.fitTo]);

  // Frame the board once, when the zones first arrive. fitTo reports false if
  // the surface has not been measured yet, so retry on the next frame rather
  // than recording a fit that never happened.
  const framed = useRef(false);
  useEffect(() => {
    if (framed.current || status !== "ready" || !boardContentBox) return;
    let frame = 0;
    const attempt = () => {
      if (framed.current) return;
      const first = boardClients[0];
      if ((first && fitClientOnCompactScreen(first)) || view.fitTo(boardContentBox)) framed.current = true;
      else frame = requestAnimationFrame(attempt);
    };
    attempt();
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, boardClients, boardContentBox, fitClientOnCompactScreen, view.fitTo]);

  /** Empty canvas includes the open part of a client's stage bands. */
  const isCanvasTouch = useCallback((target: EventTarget | null) => {
    const element = target as HTMLElement | null;
    return !element?.closest(
      ".sticky, .zone__header, .attachment, .boardtext, .inkcapture, .textcapture, button, input, textarea, select, a",
    );
  }, []);

  const beginSurfaceTouch = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "touch" || tool !== "select") return;
    const onCanvas = isCanvasTouch(event.target);
    const beganPinch = view.touchStart(event, onCanvas);
    if (beganPinch) {
      clearLongPress();
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (!onCanvas) return;

    clearLongPress();
    const pointer = event.pointerId;
    const x = event.clientX;
    const y = event.clientY;
    const timer = window.setTimeout(() => {
      if (longPress.current?.pointer !== pointer) return;
      longPress.current = null;
      view.cancelPan(pointer);
      const box = surface.current?.getBoundingClientRect();
      setMenu({ x: x - (box?.left ?? 0), y: y - (box?.top ?? 0) });
    }, 560);
    longPress.current = { pointer, x, y, timer };
  }, [clearLongPress, isCanvasTouch, tool, view]);

  const moveSurfaceTouch = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "touch") return;
    const active = longPress.current;
    if (active?.pointer === event.pointerId
      && Math.hypot(event.clientX - active.x, event.clientY - active.y) > 9) {
      clearLongPress(event.pointerId);
    }
    if (view.touchMove(event)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, [clearLongPress, view]);

  const finishSurfaceTouch = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== "touch") return;
    clearLongPress(event.pointerId);
    if (view.touchEnd(event)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, [clearLongPress, view]);

  const taskDropAt = useCallback((clientX: number, clientY: number) => {
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return null;
    for (const element of document.elementsFromPoint(clientX, clientY)) {
      const band = (element as HTMLElement).closest<HTMLElement>("[data-task-drop]");
      if (!band) continue;
      const kind = band.dataset.taskDrop;
      const id = band.dataset.dropId;
      const stage = band.dataset.dropStage as Stage | undefined;
      if ((kind === "client" || kind === "worklist") && id && stage) {
        return { kind: kind as "client" | "worklist", id, stage };
      }
    }
    return null;
  }, []);

  const dragTaskOver = useCallback((_taskId: string, clientX: number, clientY: number) => {
    setDropTarget(taskDropAt(clientX, clientY));
  }, [taskDropAt]);

  const dropTask = useCallback((taskId: string, clientX: number, clientY: number) => {
    const target = taskDropAt(clientX, clientY);
    setDropTarget(null);
    if (!target) return;
    const task = useBoard.getState().tasks.find((candidate) => candidate.id === taskId);
    if (!task) return;
    let write: Promise<void>;
    if (target.kind === "worklist") {
      write = task.worklist_id === target.id
        ? moveTaskStage(taskId, target.stage)
        : linkTaskToWorklist(taskId, target.id, target.stage);
    } else if (target.id === task.client_id) {
      write = moveTaskStage(taskId, target.stage);
    } else {
      setDragProblem("Client tasks can be linked to a worklist, but not copied into another client.");
      window.setTimeout(() => setDragProblem(""), 3200);
      return;
    }
    write.catch(() => {
      setDragProblem("That linked move didn’t save. The sticky has been put back.");
      window.setTimeout(() => setDragProblem(""), 3200);
    });
  }, [linkTaskToWorklist, moveTaskStage, taskDropAt]);

  return (
    <div className="room">
      <ObjectDefs />
      <div className="device">
        <div className="device__rail">
          {status === "ready" ? (
            <TaskFilterBar
              filters={filters}
              clients={clients}
              users={users}
              focusedClientId={focusedClientId}
              visibleCount={filteredTasks.length}
              totalCount={scopedTaskCount}
              onChange={setFilters}
            />
          ) : null}
          <img className="device__brand" src="/smrt-board.png" alt="smrt board" />
          <div className="device__account">
            {user.avatar_url ? <img className="avatar" src={user.avatar_url} alt="" /> : null}
            <span className="device__name">{user.name}</span>
            <span className="pill">{user.role}</span>
          </div>
          {/* Last in the rail, so it sits in the right-hand corner beside the
              account it now signs out. */}
          <RailMenu
            canAddZone={isAdmin}
            onAddZone={() => setAdding(true)}
            onAddWorklist={() => setAddingWorklist(true)}
            onPeople={() => setTeamOpen(true)}
            onRecycle={() => setRecycleOpen(true)}
            recycleCount={recycleCount}
            onFit={() => view.fitTo(activeContentBox)}
            onSignOut={async () => {
              await api.signOut();
              window.location.href = "/";
            }}
          />
        </div>

        <div className="device__well">
          <div
            className={
              `surface${view.panning ? " surface--panning" : ""}` +
              (view.pinching ? " surface--pinching" : "") +
              (spaceHeld ? " surface--grab" : "")
            }
            ref={surface}
            // Capture, so the press never reaches the zone, note, ink layer or
            // attachment underneath it. Held space means the whole surface is a
            // handle, whatever is drawn on top of it.
            onPointerDownCapture={(event) => {
              beginSurfaceTouch(event);
              if (event.isPropagationStopped()) return;
              if (!spaceHeld || event.button !== 0) return;
              event.stopPropagation();
              event.preventDefault();
              view.startPan(event);
            }}
            onPointerDown={(event) => {
              if (event.button !== 0) return;
              setMenu(null);
              selectObject(null);
              const target = event.target as HTMLElement;
              const touchCanvas = event.pointerType === "touch" && tool === "select" && isCanvasTouch(target);
              if (!touchCanvas && target.closest(".zone, button, input, textarea, select, a")) return;
              select(null);
              view.startPan(event);
            }}
            onPointerMoveCapture={moveSurfaceTouch}
            onPointerUpCapture={finishSurfaceTouch}
            onPointerCancelCapture={finishSurfaceTouch}
            onPointerMove={view.movePan}
            onPointerUp={view.endPan}
            onPointerCancel={view.endPan}
            onContextMenu={(event) => {
              event.preventDefault();
              const box = surface.current?.getBoundingClientRect();
              setMenu({
                x: event.clientX - (box?.left ?? 0),
                y: event.clientY - (box?.top ?? 0),
              });
            }}
          >
            <div
              className="world"
              style={{
                transform: `translate(${view.transform.x}px, ${view.transform.y}px) scale(${view.transform.scale})`,
              }}
            >
              {shown.map((client) => (
                <Zone
                  key={client.id}
                  client={client}
                  scale={view.transform.scale}
                  visibleTaskIds={visibleTaskIds}
                  onFocus={() => enterClient(client.id)}
                  onEdit={() => setEditingId(client.id)}
                  // Double-clicking a note: focus the client, then open the
                  // note. In that order, because focusing clears the open note
                  // — a client's board arriving with someone else's task still
                  // on screen would be worse than the extra call.
                  onExpandTask={(taskId) => expandTask(client.id, taskId)}
                  canResize={!focused}
                  completedExpanded={expandedCompleted.has(completedKey("client", client.id))}
                  onToggleCompleted={() => toggleCompleted(completedKey("client", client.id))}
                  dropTarget={dropTarget}
                  onTaskDragOver={dragTaskOver}
                  onTaskDrop={dropTask}
                />
              ))}
              {shownWorklists.map((worklist) => {
                const linkedIds = new Set(visibleTaskWorklists
                  .filter((link) => link.worklist_id === worklist.id)
                  .map((link) => link.task_id));
                const worklistTasks = filteredTasks.filter((task) =>
                  task.worklist_id === worklist.id || linkedIds.has(task.id));
                return <WorklistZone key={worklist.id} worklist={worklist} tasks={worklistTasks}
                  scale={view.transform.scale} dropTarget={dropTarget}
                  onTaskDragOver={dragTaskOver} onTaskDrop={dropTask}
                  onEdit={() => setEditingWorklistId(worklist.id)}
                  completedExpanded={expandedCompleted.has(completedKey("worklist", worklist.id))}
                  onToggleCompleted={() => toggleCompleted(completedKey("worklist", worklist.id))}
                  onExpandTask={(taskId) => {
                    openTask(taskId);
                  }} />;
              })}
            </div>

            <InkLayer
              transform={view.transform}
              screenToWorld={view.screenToWorld}
              regions={inkRegions}
              focusedClientId={focusedClientId}
            />
            <TextLayer
              transform={view.transform}
              screenToWorld={view.screenToWorld}
              regions={inkRegions}
              focusedClientId={focusedClientId}
            />
            <AttachmentLayer
              transform={view.transform}
              screenToWorld={view.screenToWorld}
              regions={inkRegions}
              focusedClientId={focusedClientId}
              attachPoint={attachPoint}
              onAttachHandled={() => setAttachPoint(null)}
            />

            {focused ? <LeaveFocus onLeave={() => leaveClient()} /> : null}

            {status === "ready" && clients.length === 0 && worklists.length === 0 ? (
              <p className="surface__empty">
                Nothing on the board yet. Take a pen off the rail, or right-click.
              </p>
            ) : null}
            {status === "error" ? (
              <p className="surface__empty" role="alert">
                Couldn’t load the board. Refresh to try again.
              </p>
            ) : null}

            {menu ? (
              <BoardMenu
                at={menu}
                canAddZone={isAdmin}
                onClose={() => setMenu(null)}
                onAddZone={() => setAdding(true)}
                onFit={() => view.fitTo(activeContentBox)}
                // The menu is positioned relative to the surface; the point it
                // was opened at has to go back through the same origin to
                // become a board coordinate.
                onAttachHere={() => {
                  const box = surface.current?.getBoundingClientRect();
                  setAttachPoint(view.screenToWorld(
                    menu.x + (box?.left ?? 0),
                    menu.y + (box?.top ?? 0),
                  ));
                }}
              />
            ) : null}
          </div>
        </div>

        <Zoomer
          onZoomIn={view.zoomIn}
          onZoomOut={view.zoomOut}
          onFit={() => view.fitTo(activeContentBox)}
          scale={view.transform.scale}
        />
        <HistoryControls canUndo={canUndo} canRedo={canRedo} busy={historyBusy}
          onUndo={() => void undo()} onRedo={() => void redo()} />
        {historyProblem ? <p className="historyproblem" role="alert">{historyProblem}</p> : null}
      </div>

      <Tray />
      {adding ? <AddZone onDone={() => setAdding(false)} /> : null}
      {addingWorklist ? <WorklistDialog onDone={() => setAddingWorklist(false)} /> : null}
      {editingWorklistId ? <WorklistDialog worklistId={editingWorklistId}
        onDone={() => setEditingWorklistId(null)} /> : null}
      {editingClient ? (
        <ClientEditor client={editingClient} onDone={() => setEditingId(null)} />
      ) : null}
      <TaskSheetHost onExpand={(task) => task.client_id ? expandTask(task.client_id, task.id) : openTask(task.id)} />
      {teamOpen ? <TeamManager me={user} onDone={() => setTeamOpen(false)} /> : null}
      {recycleOpen ? <RecycleBin me={user} onDone={() => setRecycleOpen(false)} /> : null}
      {dragProblem ? <p className="dragproblem" role="alert">{dragProblem}</p> : null}
    </div>
  );
}

/**
 * Back to the whole board, drawn on it rather than laid over it.
 *
 * The seed is fixed rather than taken from the client, so the arrow is the same
 * arrow every time you leave a board — a control that redrew itself per client
 * would read as six different controls.
 */
function LeaveFocus({ onLeave }: { onLeave: () => void }) {
  const arrow = useMemo(() => arrowPath("leave-focus"), []);
  return (
    <button className="leavefocus" onClick={onLeave} title="All clients (Esc)"
      aria-label="Back to all clients" aria-keyshortcuts="Escape">
      <svg width={arrow.width} height={arrow.height} viewBox={arrow.viewBox} aria-hidden="true">
        <path d={arrow.d} fill="var(--ink-marker-blue)" />
      </svg>
    </button>
  );
}

/**
 * The board's own menu, in the corner of the frame. Things that are about the
 * board rather than about drawing on it live here; the rail below holds only
 * the objects you pick up.
 */
/**
 * A person, rather than a chess pawn. The old glyph was whatever the font
 * offered; this is a head and a pair of shoulders, which is what people read
 * "users" as without having to be told.
 */
function PersonObject() {
  return (
    <svg className="obj obj--person" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="6.4" r="3.6" fill="#5b646d" />
      <path d="M2.6 18.4a7.4 7.4 0 0 1 14.8 0Z" fill="#5b646d" />
    </svg>
  );
}

function TrashObject() {
  return (
    <svg className="obj obj--trash" width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M5.2 6.2h9.6l-.7 10.2H5.9L5.2 6.2Z" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3.8 4.5h12.4M7.4 4.5l.5-1.7h4.2l.5 1.7M8 8.2v5.9M12 8.2v5.9" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function RailMenu({
  canAddZone,
  onAddZone,
  onAddWorklist,
  onPeople,
  onRecycle,
  recycleCount,
  onFit,
  onSignOut,
}: {
  /** Only an admin may create a client, so only an admin is offered it. */
  canAddZone: boolean;
  onAddZone: () => void;
  onAddWorklist: () => void;
  onPeople: () => void;
  onRecycle: () => void;
  recycleCount: number;
  onFit: () => void;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: MouseEvent) => {
      if (!(event.target as HTMLElement).closest(".railmenu")) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const id = window.setTimeout(() => window.addEventListener("mousedown", dismiss));
    window.addEventListener("keydown", escape);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("mousedown", dismiss);
      window.removeEventListener("keydown", escape);
    };
  }, [open]);

  const run = (fn: () => void) => () => {
    fn();
    setOpen(false);
  };

  return (
    <div className="railmenu">
      <button
        className="railmenu__button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Board menu"
        title="Board menu"
      >
        <span aria-hidden="true" />
        <span aria-hidden="true" />
        <span aria-hidden="true" />
      </button>

      {open ? (
        <div className="railmenu__list" role="menu">
          {canAddZone ? (
            <>
              <button className="menu__item" role="menuitem" onClick={run(onAddZone)}>
                <ZoneObject />
                New client
              </button>
              <button className="menu__item" role="menuitem" onClick={run(onAddWorklist)}>
                <span className="menu__glyph">↗</span>
                New worklist
              </button>
              <button className="menu__item" role="menuitem" onClick={run(onPeople)}>
                <PersonObject />
                Users
              </button>
            </>
          ) : null}
          <button className="menu__item" role="menuitem" onClick={run(onRecycle)}>
            <TrashObject />
            Recycle bin
            {recycleCount > 0 ? <span className="railmenu__badge">{recycleCount > 99 ? "99+" : recycleCount}</span> : null}
          </button>
          <button className="menu__item" role="menuitem" onClick={run(onFit)}>
            Zoom to fit
          </button>
          <span className="menu__rule" />
          <button className="menu__item" role="menuitem" onClick={run(onSignOut)}>
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* the rail                                                                    */
/* -------------------------------------------------------------------------- */

function Tray() {
  const tool = useBoard((s) => s.tool);
  const markerColor = useBoard((s) => s.markerColor);
  const setMarkerColor = useBoard((s) => s.setMarkerColor);
  const setTool = useBoard((s) => s.setTool);
  const [palette, setPalette] = useState(false);

  const pick = (next: Tool) => setTool(tool === next ? "select" : next);

  return (
    <div className="tray" role="toolbar" aria-label="Board tools">
      <span className="tray__cap tray__cap--left" aria-hidden="true" />
      <span className="tray__cap tray__cap--right" aria-hidden="true" />

      <div className="item__slot">
        <button
          className={`item${tool === "pen" ? " item--held" : ""}`}
          onClick={() => {
            // A phone has no right mouse button. Tapping the marker while it is
            // already in hand opens the same colour choices instead of putting
            // it away; desktop right-click remains available below.
            if (tool === "pen") setPalette((open) => !open);
            else pick("pen");
          }}
          onContextMenu={(event) => {
            // Right-click loads a different colour. The pen keeps whichever it
            // is holding, so the rail shows the choice without a second pen.
            event.preventDefault();
            setPalette((open) => !open);
          }}
          title="Marker — tap again or right-click to change colour"
          aria-keyshortcuts="M"
          aria-pressed={tool === "pen"}
          aria-label="Marker"
        >
          <Marker id={markerColor} color={colorOf(markerColor)} />
        </button>

        {palette ? (
          <Palette
            selected={markerColor}
            onPick={(id) => {
              setMarkerColor(id);
              setPalette(false);
            }}
            onClose={() => setPalette(false)}
          />
        ) : null}
      </div>

      <button
        className={`item${tool === "eraser" ? " item--held" : ""}`}
        onClick={() => pick("eraser")}
        title="Eraser"
        aria-pressed={tool === "eraser"}
        aria-label="Eraser"
        aria-keyshortcuts="E"
      >
        <EraserObject />
      </button>

      <button
        className={`item${tool === "sticky" ? " item--held" : ""}`}
        onClick={() => pick("sticky")}
        title="Sticky note"
        aria-pressed={tool === "sticky"}
        aria-label="Sticky note"
        aria-keyshortcuts="S"
      >
        <StickyObject />
      </button>

      <button
        className={`item item--text${tool === "text" ? " item--held" : ""}`}
        onClick={() => pick("text")}
        title="Text"
        aria-pressed={tool === "text"}
        aria-label="Text"
        aria-keyshortcuts="T"
      >
        <TextObject />
      </button>

      <button
        className={`item${tool === "attach" ? " item--held" : ""}`}
        onClick={() => pick("attach")}
        title="Attach a file"
        aria-pressed={tool === "attach"}
        aria-label="Attach a file"
        aria-keyshortcuts="A"
      >
        <ClipObject />
      </button>
    </div>
  );
}

/** The four colours the one pen can be loaded with. */
function Palette({
  selected,
  onPick,
  onClose,
}: {
  selected: MarkerColor;
  onPick: (id: MarkerColor) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const dismiss = (event: MouseEvent) => {
      if (!(event.target as HTMLElement).closest(".palette")) onClose();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    // Deferred to the next tick: the click that opened this would otherwise be
    // the click that closes it.
    const id = window.setTimeout(() => window.addEventListener("mousedown", dismiss));
    window.addEventListener("keydown", escape);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("mousedown", dismiss);
      window.removeEventListener("keydown", escape);
    };
  }, [onClose]);

  return (
    <div className="palette" role="menu" aria-label="Marker colour">
      {MARKER_COLORS.map((marker) => (
        <button
          key={marker.id}
          className={`palette__chip${selected === marker.id ? " palette__chip--on" : ""}`}
          style={{ background: marker.color }}
          onClick={() => onPick(marker.id)}
          title={marker.label}
          aria-label={marker.label}
          role="menuitemradio"
          aria-checked={selected === marker.id}
        />
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* the objects that lie on it                                                  */
/* -------------------------------------------------------------------------- */

/**
 * A dry-wipe marker as it lies in the rail's channel: white barrel, coloured
 * cap, brand printed along the side. Drawn slim and long, which is what you
 * actually see of a pen resting in a whiteboard rail.
 */
function Marker({ id, color }: { id: string; color: string }) {
  const barrel = `barrel-${id}`;
  return (
    <svg className="obj" width="76" height="15" viewBox="0 0 76 15" aria-hidden="true">
      <defs>
        <linearGradient id={barrel} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#c9ced3" />
          <stop offset="12%" stopColor="#ffffff" />
          <stop offset="48%" stopColor="#f6f7f8" />
          <stop offset="82%" stopColor="#dfe3e7" />
          <stop offset="100%" stopColor="#b6bcc2" />
        </linearGradient>
      </defs>

      {/* barrel */}
      <rect x="14" y="0.5" width="61" height="14" rx="3" fill={`url(#${barrel})`} />
      {/* the printed brand */}
      <text
        x="46"
        y="10.2"
        textAnchor="middle"
        fontSize="7"
        fontWeight="700"
        letterSpacing="0.4"
        fill="#7b8288"
        fontFamily="ui-sans-serif, system-ui, sans-serif"
      >
        smrt
      </text>
      {/* the collar ring where the cap seats */}
      <rect x="16.5" y="0.5" width="1.4" height="14" fill="#000" opacity="0.14" />
      {/* cap */}
      <rect x="0" y="0" width="19" height="15" rx="3.5" fill={color} />
      <rect x="0" y="0" width="19" height="15" rx="3.5" fill="url(#cap-shade)" />
      {/* clip on the cap */}
      <rect x="4" y="0.8" width="11" height="2.6" rx="1.3" fill="#fff" opacity="0.22" />
    </svg>
  );
}

/** Shared cylinder shading, defined once for every object on the rail. */
function ObjectDefs() {
  return (
    <svg width="0" height="0" aria-hidden="true" style={{ position: "absolute" }}>
      <defs>
        <linearGradient id="cap-shade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#000" stopOpacity="0.3" />
          <stop offset="12%" stopColor="#fff" stopOpacity="0.4" />
          <stop offset="34%" stopColor="#fff" stopOpacity="0.1" />
          <stop offset="70%" stopColor="#000" stopOpacity="0.14" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.42" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/**
 * A pink wedge eraser, the shape everyone has held: a bar with both ends cut
 * back at an angle, so the top face is shorter than the base. Drawn from
 * slightly above, like the reference, which is what gives it volume at this
 * size. Colours sampled off the photograph rather than guessed.
 */
function EraserObject() {
  return (
    <svg className="obj" width="53" height="20" viewBox="3 3 53 20" aria-hidden="true">
      <defs>
        <linearGradient id="er-face" x1="0" y1="0" x2="1" y2="0.35">
          <stop offset="0%" stopColor="#fb9dab" />
          <stop offset="45%" stopColor="#f28f9e" />
          <stop offset="100%" stopColor="#cd6b78" />
        </linearGradient>
        <linearGradient id="er-top" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ffb8c2" />
          <stop offset="65%" stopColor="#fca6b2" />
          <stop offset="100%" stopColor="#eb909e" />
        </linearGradient>
        <linearGradient id="er-end" x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0%" stopColor="#f59aa8" />
          <stop offset="100%" stopColor="#d97c8c" />
        </linearGradient>
      </defs>

      {/*
        A block sliced twice on the same diagonal, drawn as three facets so it
        has volume: the top, the long front, and the cut end at the left. The
        back of the block is offset up and to the left, which is what puts that
        end face in view.
      */}
      {/* top */}
      <path d="M15 3 L51 3 L56 8 L20 8 Z" fill="url(#er-top)" />
      {/* the cut end at the left */}
      <path d="M15 3 L20 8 L8 23 L3 18 Z" fill="url(#er-end)" />
      {/* the long front face */}
      <path d="M20 8 L56 8 L44 23 L8 23 Z" fill="url(#er-face)" />

      {/* the creases between facets, and a darker foot */}
      <path d="M20 8 L56 8" stroke="#fff" strokeOpacity="0.45" strokeWidth="0.9" />
      <path d="M15 3 L20 8" stroke="#fff" strokeOpacity="0.35" strokeWidth="0.7" />
      <path d="M20 8 L8 23" stroke="#b8697a" strokeOpacity="0.45" strokeWidth="0.8" />
      <path d="M8 23 L44 23" stroke="#a95666" strokeOpacity="0.5" strokeWidth="1" />

      {/* printed on the front face, swoosh and all */}
      <path
        d="M18 13.6c7-2.6 17-2.6 24 0.4"
        fill="none"
        stroke="#2f3033"
        strokeWidth="1.1"
        strokeLinecap="round"
      />
      <text
        x="31"
        y="20.4"
        textAnchor="middle"
        fontSize="7.6"
        fontWeight="700"
        letterSpacing="0.3"
        fill="#2f3033"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontStyle="italic"
      >
        smrt
      </text>
    </svg>
  );
}

/** A pad of sticky notes, top one lifting slightly. */
function StickyObject() {
  return (
    <svg className="obj" width="27" height="26" viewBox="1 3 27 26" aria-hidden="true">
      <defs>
        <linearGradient id="sticky-face" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffe27a" />
          <stop offset="70%" stopColor="#fdd44f" />
          <stop offset="100%" stopColor="#eab933" />
        </linearGradient>
      </defs>
      {/* the pad underneath */}
      <rect x="3" y="6" width="25" height="23" rx="1.5" fill="#e0bb3c" />
      <rect x="2" y="4.5" width="25" height="23" rx="1.5" fill="#eec847" />
      {/* the top sheet */}
      <rect x="1" y="3" width="25" height="23" rx="1.5" fill="url(#sticky-face)" />
      {/* ruled lines, so it reads as something written on */}
      <g stroke="#c9a02c" strokeWidth="1" strokeLinecap="round" opacity="0.55">
        <path d="M5 10h17" />
        <path d="M5 14h17" />
        <path d="M5 18h11" />
      </g>
    </svg>
  );
}

/** The text tool, as a printed label tile lying in the rail. */
function TextObject() {
  return (
    <svg className="obj" width="24" height="24" viewBox="1 1 24 24" aria-hidden="true">
      <defs>
        <linearGradient id="tile-face" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="70%" stopColor="#f2f4f6" />
          <stop offset="100%" stopColor="#dbe0e5" />
        </linearGradient>
      </defs>
      <rect x="1" y="1" width="24" height="24" rx="3.5" fill="url(#tile-face)" />
      <rect x="1" y="1" width="24" height="24" rx="3.5" fill="none" stroke="#b9c0c7" />
      <text
        x="13"
        y="19"
        textAnchor="middle"
        fontSize="17"
        fontWeight="700"
        fill="#2c3238"
        fontFamily="Georgia, 'Times New Roman', serif"
      >
        T
      </text>
    </svg>
  );
}

/**
 * A paperclip, for attaching files. Bent from one length of wire, so it is one
 * stroked path — the inner loop returning inside the outer one is what makes a
 * paperclip legible at any size.
 */
function ClipObject() {
  return (
    <svg className="obj obj--clip" width="20" height="33" viewBox="-2.65 2.67 22.31 36.65" aria-hidden="true">
      <defs>
        <linearGradient id="clip-wire" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#7d858d" />
          <stop offset="35%" stopColor="#d3d9de" />
          <stop offset="60%" stopColor="#9aa2aa" />
          <stop offset="100%" stopColor="#6d757d" />
        </linearGradient>
      </defs>
      {/*
        One length of wire, bent three times: down the inner leg, round the
        small bottom loop, up and over the top, down the long outer leg, round
        the wide bottom loop and back up. The two nested U-turns are what make
        it read as a paperclip instead of a hook.
      */}
      {/* Tipped to the right, the way one actually ends up lying in a tray.
          The viewBox is opened up around the pivot to take the swing. */}
      <g transform="rotate(20 8.5 21)">
        <path
          d="M11.5 9 L11.5 30 A3.25 3.25 0 0 1 5 30 L5 8 A4.5 4.5 0 0 1 14 8 L14 33 A5.5 5.5 0 0 1 3 33 L3 13"
          fill="none"
          stroke="url(#clip-wire)"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* a highlight running down the lit side of the wire */}
        <path
          d="M11.5 11 L11.5 28"
          fill="none"
          stroke="#ffffff"
          strokeOpacity="0.5"
          strokeWidth="0.65"
          strokeLinecap="round"
        />
      </g>
    </svg>
  );
}

/** A new client zone, as a blank card. */
function ZoneObject() {
  return (
    <svg className="obj" width="30" height="24" viewBox="0 0 30 24" aria-hidden="true">
      <rect x="1" y="1" width="28" height="22" rx="3" fill="#ffffff" stroke="#b9c0c7" />
      <path d="M1 7h28" stroke="#d3d9df" />
      <g stroke="#c3cad1" strokeWidth="1.4" strokeLinecap="round">
        <path d="M15 11v8M11 15h8" />
      </g>
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* chrome                                                                      */
/* -------------------------------------------------------------------------- */

function Zoomer({
  onZoomIn,
  onZoomOut,
  onFit,
  scale,
}: {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFit: () => void;
  scale: number;
}) {
  return (
    <div className="zoomer">
      <button className="zoomer__key" onClick={onZoomOut} title="Zoom out" aria-label="Zoom out">
        −
      </button>
      <button className="zoomer__key zoomer__key--wide" onClick={onFit} title="Zoom to fit">
        {Math.round(scale * 100)}%
      </button>
      <button className="zoomer__key" onClick={onZoomIn} title="Zoom in" aria-label="Zoom in">
        +
      </button>
    </div>
  );
}

function HistoryControls({ canUndo, canRedo, busy, onUndo, onRedo }: {
  canUndo: boolean;
  canRedo: boolean;
  busy: boolean;
  onUndo: () => void;
  onRedo: () => void;
}) {
  return <div className="historycontrols" aria-label="Change history">
    {/* Disabled only when there is nothing left to undo. A write in flight
        shows as busy, but still accepts the press — the store queues it. */}
    <button className="zoomer__key" disabled={!canUndo} onClick={onUndo}
      title="Undo (Ctrl+Z)" aria-label="Undo">↶</button>
    <button className="zoomer__key" disabled={!canRedo} onClick={onRedo}
      title="Redo (Ctrl+Shift+Z)" aria-label="Redo">↷</button>
  </div>;
}

/** Everything on the rail, where the pointer already is. */
function BoardMenu({
  at,
  canAddZone,
  onClose,
  onAddZone,
  onAttachHere,
  onFit,
}: {
  at: { x: number; y: number };
  canAddZone: boolean;
  onClose: () => void;
  onAddZone: () => void;
  /** Attach a file at the point this menu was opened at. */
  onAttachHere: () => void;
  onFit: () => void;
}) {
  const tool = useBoard((s) => s.tool);
  const markerColor = useBoard((s) => s.markerColor);
  const setMarkerColor = useBoard((s) => s.setMarkerColor);
  const setTool = useBoard((s) => s.setTool);
  const root = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(at);

  useLayoutEffect(() => {
    const menu = root.current;
    const parent = menu?.offsetParent as HTMLElement | null;
    if (!menu || !parent) return;
    const gutter = 8;
    setPosition({
      x: Math.max(gutter, Math.min(at.x, parent.clientWidth - menu.offsetWidth - gutter)),
      y: Math.max(gutter, Math.min(at.y, parent.clientHeight - menu.offsetHeight - gutter)),
    });
  }, [at]);

  const close = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const onDown = () => close();
    window.addEventListener("keydown", onKey);
    // Anything clicked outside the menu dismisses it; the menu stops its own
    // pointer events from reaching here.
    window.addEventListener("pointerdown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onDown);
    };
  }, [close]);

  const run = (fn: () => void) => () => {
    fn();
    close();
  };

  return (
    <div
      ref={root}
      className="menu"
      style={{ left: position.x, top: position.y }}
      onPointerDown={(event) => event.stopPropagation()}
      onContextMenu={(event) => event.preventDefault()}
      role="menu"
    >
      {canAddZone ? (
        <button className="menu__item" role="menuitem" onClick={run(onAddZone)}>
          <ZoneObject />
          New client zone
        </button>
      ) : null}
      <button className="menu__item" role="menuitem" onClick={run(() => setTool("sticky"))}>
        <StickyObject />
        Sticky note <kbd>S</kbd>
      </button>
      <button className="menu__item" role="menuitem" onClick={run(() => setTool("text"))}>
        <TextObject />
        Text <kbd>T</kbd>
      </button>
      {/* Right-clicking names the spot, so this attaches there rather than
          taking the tool and letting it choose. */}
      <button className="menu__item" role="menuitem" onClick={run(onAttachHere)}>
        <ClipObject />
        Attach a file here <kbd>A</kbd>
      </button>

      <span className="menu__rule" />

      {/* One pen. The colours sit beside it rather than each being its own
          entry, which is the same choice the rail makes. */}
      <button
        className={`menu__item${tool === "pen" ? " menu__item--on" : ""}`}
        role="menuitem"
        onClick={run(() => setTool("pen"))}
      >
        <Marker id={markerColor} color={colorOf(markerColor)} />
        Marker <kbd>M</kbd>
      </button>
      <div className="menu__colors">
        {MARKER_COLORS.map((marker) => (
          <button
            key={marker.id}
            className={`palette__chip${markerColor === marker.id ? " palette__chip--on" : ""}`}
            style={{ background: marker.color }}
            title={marker.label}
            aria-label={marker.label}
            onClick={() => setMarkerColor(marker.id)}
          />
        ))}
      </div>
      <button
        className={`menu__item${tool === "eraser" ? " menu__item--on" : ""}`}
        role="menuitem"
        onClick={run(() => setTool("eraser"))}
      >
        <EraserObject />
        Eraser <kbd>E</kbd>
      </button>

      <span className="menu__rule" />

      <button className="menu__item" role="menuitem" onClick={run(onFit)}>
        <span className="menu__glyph">⤢</span>
        Zoom to fit
      </button>
    </div>
  );
}

function WorklistDialog({ onDone, worklistId }: { onDone: () => void; worklistId?: string }) {
  const worklist = useBoard((state) => state.worklists.find((item) => item.id === worklistId));
  const createWorklist = useBoard((state) => state.createWorklist);
  const updateWorklist = useBoard((state) => state.updateWorklist);
  const setWorklistLogo = useBoard((state) => state.setWorklistLogo);
  const clearWorklistLogo = useBoard((state) => state.clearWorklistLogo);
  const [name, setName] = useState(worklist?.name ?? "");
  const [logo, setLogo] = useState<string | null>(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");

  async function pick(file: File | null) {
    if (!file) return;
    setProblem("");
    try { setLogo(await shrinkImage(file)); setRemoveLogo(false); }
    catch { setProblem("That logo couldn’t be prepared."); }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const value = name.trim();
    if (!value || busy) return;
    setBusy(true);
    setProblem("");
    try {
      const saved = worklist ?? await createWorklist(value);
      if (worklist) await updateWorklist(worklist.id, { name: value });
      if (logo) await setWorklistLogo(saved.id, logo);
      else if (removeLogo && saved.logo_updated_at) await clearWorklistLogo(saved.id);
      onDone();
    } catch {
      setProblem("That worklist didn’t save.");
      setBusy(false);
    }
  }

  return <form className="addzone addzone--worklist" onSubmit={submit}>
    <label className="addzone__logo" title="Choose worklist logo">
      {logo ? <img src={logo} alt="New logo preview" /> : worklist?.logo_updated_at && !removeLogo
        ? <img src={`/api/worklists/${worklist.id}/logo?v=${encodeURIComponent(worklist.logo_updated_at)}`} alt="Current logo" />
        : <span>Logo</span>}
      <input type="file" accept="image/png,image/jpeg,image/webp"
        onChange={(event) => void pick(event.target.files?.[0] ?? null)} />
    </label>
    <input className="addzone__input" autoFocus value={name} maxLength={80}
      placeholder="Worklist name, e.g. Internal To-do" onChange={(event) => setName(event.target.value)}
      onKeyDown={(event) => { if (event.key === "Escape") onDone(); }} />
    <button className="addzone__go" type="submit" disabled={busy || !name.trim()}>
      {worklist ? "Save" : "Add"}
    </button>
    <button className="addzone__cancel" type="button" onClick={onDone}>Cancel</button>
    {worklist?.logo_updated_at && !removeLogo && !logo ? <button className="addzone__cancel" type="button"
      onClick={() => setRemoveLogo(true)}>Remove logo</button> : null}
    {problem ? <p className="addzone__problem" role="alert">{problem}</p> : null}
  </form>;
}

/** Naming a new client zone. */
function AddZone({ onDone }: { onDone: () => void }) {
  const createClient = useBoard((s) => s.createClient);
  const setClientLogo = useBoard((s) => s.setClientLogo);
  const [name, setName] = useState("");
  const [status, setStatus] = useState("");
  const [logo, setLogo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function pick(file: File | null) {
    setProblem(null);
    if (!file) return setLogo(null);
    try {
      setLogo(await shrinkImage(file));
    } catch {
      setProblem("Couldn’t read that image.");
      setLogo(null);
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      // Two requests, deliberately: the client exists the moment it is created,
      // so a logo that fails to upload costs the logo and not the client.
      const client = await createClient({ name: trimmed, status: status.trim() || null });
      if (logo) {
        await setClientLogo(client.id, logo).catch(() => {
          setProblem("Added the client, but the logo wouldn’t upload.");
          throw new Error("logo");
        });
      }
      onDone();
    } catch {
      // The message is already set where it matters; leave the form open.
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="addzone" onSubmit={submit}>
      <input
        className="addzone__input"
        autoFocus
        value={name}
        maxLength={80}
        placeholder="Client name"
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") onDone();
        }}
      />
      <input
        className="addzone__input addzone__input--status"
        value={status}
        maxLength={40}
        list="client-statuses-new"
        placeholder="Status"
        onChange={(event) => setStatus(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") onDone();
        }}
      />
      {/* Suggestions only — status is free text, and the editor offers the same. */}
      <datalist id="client-statuses-new">
        <option value="Active" />
        <option value="Waiting" />
        <option value="On hold" />
        <option value="Complete" />
      </datalist>
      <label className="addzone__logo" title="Add a logo">
        {logo ? <img src={logo} alt="" /> : <span>Logo</span>}
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          onChange={(event) => void pick(event.target.files?.[0] ?? null)}
        />
      </label>
      <button className="addzone__go" type="submit" disabled={busy || !name.trim()}>
        Add
      </button>
      <button className="addzone__cancel" type="button" onClick={onDone}>
        Cancel
      </button>
      {problem ? <p className="addzone__problem">{problem}</p> : null}
    </form>
  );
}
